import { getPlaceDetails, getPlacesPredictions } from '@services/google/places';
import { DEFINITIONS } from '../utils/constants';
import { refreshToken } from '../utils/token';
import { getTranslations } from '../utils/translations';

/**
 * @description Sets the aria-expanded attribute on the input element.
 * @param {HTMLInputElement} input - The input element to update.
 * @param {boolean} expanded - Whether the autocomplete is expanded.
 */
const setExpanded = (input, expanded) => input.setAttribute('aria-expanded', String(expanded));

/**
 * @description Sets the aria-activedescendant attribute on the input element.
 * @param {HTMLInputElement} input - The input element to update.
 * @param {string} id - The ID of the active descendant.
 */
const setActiveDescendant = (input, id) => input.setAttribute('aria-activedescendant', id ?? '');

/**
 * @description Clears the active state from all items in the results list.
 * @param {NodeListOf<Element>} items - The list of items to clear.
 * @param {HTMLInputElement} input - The input element to update.
 */
const clearActive = (items, input) => {
  items.forEach((item) => {
    item.classList.remove(DEFINITIONS.activeClass);
    item.setAttribute('aria-selected', 'false');
  });
  setActiveDescendant(input, '');
};

/**
 * @description Creates a status item for the results list.
 * @param {string} text - The text to display in the status item.
 * @returns {HTMLLIElement} The status item.
 */
const createStatusItem = (text) => {
  const li = document.createElement('li');
  li.classList.add('autocomplete__results__list__item');
  li.setAttribute('role', 'option');
  li.setAttribute('aria-disabled', 'true');
  li.textContent = text;
  return li;
};

/**
 * @description Initializes the autocomplete functionality on the given context (container element).
 * Sets up event listeners for input changes, selection, and keyboard navigation.
 * @param {HTMLElement} context - The container element for the autocomplete UI.
 * @param {Object} options - Configuration options.
 * @param {Object} options.loader - The Google Maps API loader.
 * @param {Object} options.sessionToken - The session token for autocomplete requests.
 * @param {Object} options.map - The Google Map instance.
 * @param {Object} options.mapOptions - Options for the map (language, region).
 * @param {Object} options.userPosition - The user's current position (latitude, longitude).
 * @param {Function} callback - A callback function to be called when a place is selected.
 * @returns {Promise<void>}
 */
export const initAutocomplete = async (
  context,
  { loader, map, mapOptions, userPosition, autocompleteOptions },
  callback,
) => {
  const searchInput = context.querySelector('.autocomplete__input');
  if (!searchInput) return;

  const { AutocompleteSessionToken, AutocompleteSuggestion } = await loader.importLibrary('places');
  if (!AutocompleteSessionToken || !AutocompleteSuggestion)
    return console.error('Google Maps Places library is not loaded');

  let sessionToken = new AutocompleteSessionToken();

  // Instantiate to ensure the library side-effects are ready
  new AutocompleteSuggestion();

  const translations = getTranslations(context);
  const resultsContainer = context.querySelector('.autocomplete__results');
  const resultsList = resultsContainer?.querySelector('.autocomplete__results__list');

  let currentRequest = null;

  // ─── UI helpers ───────────────────────────────────────────────────────────

  const showResults = () => {
    resultsContainer.style.display = 'block';
    setExpanded(searchInput, true);
  };

  const hideResults = () => {
    resultsContainer.style.display = 'none';
    setExpanded(searchInput, false);
    setActiveDescendant(searchInput, '');
  };

  const resetResults = () => {
    resultsList.replaceChildren();
    hideResults();
    currentRequest = null;
  };

  // ─── Item selection ────────────────────────────────────────────────────────

  const selectItem = (listItem, placePrediction) => {
    searchInput.value = placePrediction.text.text;
    clearActive(resultsList.querySelectorAll('[role="option"]'), searchInput);
    listItem.setAttribute('aria-selected', 'true');
    hideResults();

    if (placePrediction.placeId) {
      getPlaceDetails({ id: placePrediction.placeId, fields: ['location'] })
        .then((place) => {
          if (place?.location) {
            map.setCenter(place.location);
            map.setZoom(autocompleteOptions?.zoom || 13);
            callback?.(place);
          }
        })
        .catch(console.error);
    }

    refreshToken(currentRequest, AutocompleteSessionToken);
  };

  // ─── Keyboard navigation ───────────────────────────────────────────────────

  const navigateList = (direction) => {
    const items = resultsList.querySelectorAll('[role="option"]:not([aria-disabled="true"])');
    if (!items.length) return;

    const current = Array.from(items).findIndex((i) => i.classList.contains(DEFINITIONS.activeClass));
    const next = (current + direction + items.length) % items.length;

    clearActive(items, searchInput);
    items[next].classList.add(DEFINITIONS.activeClass);
    items[next].setAttribute('aria-selected', 'true');
    setActiveDescendant(searchInput, items[next].id);
    items[next].scrollIntoView({ block: 'nearest' });
  };

  // ─── Event listeners ───────────────────────────────────────────────────────

  context.querySelector('.autocomplete__clear')?.addEventListener('click', () => {
    searchInput.value = '';
    resetResults();
    searchInput.focus();
  });

  searchInput.addEventListener('input', ({ target: { value: query } }) => {
    if (query.length < 3) {
      resetResults();
      return;
    }

    // Ref. https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data?hl=it#AutocompleteRequest
    currentRequest = {
      input: query,
      sessionToken,
      language: mapOptions?.language ?? 'en',
      region: mapOptions?.region ?? 'US',
      origin: userPosition.coords
        ? new window.google.maps.LatLng(userPosition.coords.lat, userPosition.coords.lng)
        : undefined,
    };

    getPlacesPredictions(currentRequest)
      .then((suggestions) => {
        resultsList.replaceChildren();

        if (!suggestions.length) {
          resultsList.appendChild(createStatusItem(translations?.autocomplete?.no_results));
          showResults();
          return;
        }

        const fragment = document.createDocumentFragment();
        suggestions.forEach((suggestion, index) => {
          const { placePrediction } = suggestion;
          const listItem = document.createElement('li');
          listItem.id = `gmap-search-option-${index}`;
          listItem.setAttribute('role', 'option');
          listItem.setAttribute('aria-selected', 'false');
          listItem.classList.add(`${DEFINITIONS.slug}__autocomplete-result-item`, 'autocomplete__results__list__item');
          listItem.dataset.placeId = placePrediction.placeId;
          listItem.textContent = placePrediction.text.text;

          // Prevent input blur without triggering selection
          listItem.addEventListener('pointerdown', (e) => e.preventDefault());

          // Selection — fires for mouse click, touch tap, and AT activation
          listItem.addEventListener('click', () => {
            selectItem(listItem, placePrediction);
          });

          fragment.appendChild(listItem);
        });

        resultsList.appendChild(fragment);
        showResults();
      })
      .catch((error) => {
        console.error('Error fetching place predictions:', error);
        resultsList.replaceChildren(createStatusItem(translations?.autocomplete?.error));
        showResults();
      });
  });

  // Handle focus/blur for showing/hiding results
  searchInput.addEventListener('focus', () => {
    if (resultsList.children.length > 0) showResults();
  });

  searchInput.addEventListener('blur', () => {
    setTimeout(hideResults, 200);
  });

  // Handle keyboard navigation for accessibility
  searchInput.addEventListener('keydown', (event) => {
    const hasResults = resultsList.querySelector('[role="option"]:not([aria-disabled="true"])');
    if (!hasResults) return;

    const handlers = {
      ArrowDown: () => {
        event.preventDefault();
        navigateList(1);
      },
      ArrowUp: () => {
        event.preventDefault();
        navigateList(-1);
      },
      Escape: () => resetResults(),
      Enter: () => {
        const items = resultsList.querySelectorAll('[role="option"]:not([aria-disabled="true"])');
        const selectedIndex = Array.from(items).findIndex((i) => i.classList.contains(DEFINITIONS.activeClass));
        if (selectedIndex < 0) return;
        event.preventDefault();
        selectItem(items[selectedIndex], {
          text: { text: items[selectedIndex].textContent },
          placeId: items[selectedIndex].dataset.placeId,
        });
      },
    };

    handlers[event.key]?.();
  });
};
